/** SVG path generation for the Shefa Green cannabis line-art loader (from premium HTML prototype). */
export const SX = 80;
export const COLA_TOP = 14;
export const COLA_BOT = 47;

export function lancetLeaf(bx, by, tx, ty, w) {
  const dx = tx - bx;
  const dy = ty - by;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const SEGS = 16;
  const pts = [];
  for (let i = 0; i <= SEGS; i += 1) {
    const t = i / SEGS;
    const env = Math.sin(Math.PI * t) * (1 - t * 0.35);
    const serr = (i % 2 === 0 ? 0.6 : -0.25) * w * 0.22 * Math.sin(Math.PI * t);
    pts.push([bx + ux * len * t + px * (w * env + serr), by + uy * len * t + py * (w * env + serr)]);
  }
  for (let i = SEGS; i >= 0; i -= 1) {
    const t = i / SEGS;
    const env = Math.sin(Math.PI * t) * (1 - t * 0.35);
    const serr = (i % 2 === 0 ? 0.6 : -0.25) * w * 0.22 * Math.sin(Math.PI * t);
    pts.push([bx + ux * len * t - px * (w * env + serr), by + uy * len * t - py * (w * env + serr)]);
  }
  return (
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ') + ' Z'
  );
}

export function colaPath(cx, top, bot) {
  const h = bot - top;
  const w = 4.5;
  return [
    `M${cx},${bot} C${cx - w},${bot - h * 0.1} ${cx - w * 1.2},${top + h * 0.3} ${cx},${top} C${cx + w * 1.2},${top + h * 0.3} ${cx + w},${bot - h * 0.1} ${cx},${bot}`,
    `M${cx},${top + h * 0.15} L${cx - 3},${top + h * 0.3} M${cx},${top + h * 0.4} L${cx - 3.5},${top + h * 0.55}`,
    `M${cx},${top + h * 0.15} L${cx + 3},${top + h * 0.3} M${cx},${top + h * 0.4} L${cx + 3.5},${top + h * 0.55}`,
  ].join(' ');
}

const LEAF_DEFS = [
  { ay: 120, a: 47, len: 40, w: 3.5 },
  { ay: 99, a: 41, len: 35, w: 3 },
  { ay: 80, a: 34, len: 28, w: 2.4 },
  { ay: 63, a: 25, len: 21, w: 1.8 },
];

/** @typedef {{ key: string, paths: { id: string, d: string, strokeWidth: string, L: number, t: string, stagger: number }[] }} PartGroup */
/** @returns { PartGroup[] } */
export function buildPartGroups() {
  /** @type { PartGroup[] } */
  const groups = [];

  groups.push({
    key: 'pot',
    paths: [
      {
        id: 'pot-0',
        d: 'M51,174 L109,174 L114,159 L46,159 Z M43,159 L117,159',
        strokeWidth: '0.5',
        L: 290,
        t: '0.8s',
        stagger: 0,
      },
    ],
  });

  groups.push({
    key: 'stem',
    paths: [
      {
        id: 'stem-0',
        d: `M${SX},159 L${SX},${COLA_BOT}`,
        strokeWidth: '0.5',
        L: 116,
        t: '0.9s',
        stagger: 0,
      },
    ],
  });

  LEAF_DEFS.forEach((d, i) => {
    const r = (d.a * Math.PI) / 180;
    const txL = SX - Math.sin(r) * d.len;
    const txR = SX + Math.sin(r) * d.len;
    const ty = d.ay - Math.cos(r) * d.len;
    const key = `lv${i}`;
    groups.push({
      key,
      paths: [
        {
          id: `${key}-L`,
          d: lancetLeaf(SX, d.ay, txL, ty, d.w),
          strokeWidth: '0.5',
          L: 230,
          t: '0.7s',
          stagger: 0,
        },
        {
          id: `${key}-R`,
          d: lancetLeaf(SX, d.ay, txR, ty, d.w),
          strokeWidth: '0.5',
          L: 230,
          t: '0.7s',
          stagger: 150,
        },
      ],
    });
  });

  groups.push({
    key: 'cola',
    paths: [
      {
        id: 'cola-0',
        d: colaPath(SX, COLA_TOP, COLA_BOT),
        strokeWidth: '0.5',
        L: 400,
        t: '1.2s',
        stagger: 0,
      },
    ],
  });

  return groups;
}

export const STAGES = [
  { k: 'pot', th: 0, l: 'connecting', dur: 800 },
  { k: 'stem', th: 15, l: 'initializing', dur: 900 },
  { k: 'lv0', th: 30, l: 'loading', dur: 700 },
  { k: 'lv1', th: 45, l: 'fetching', dur: 700 },
  { k: 'lv2', th: 60, l: 'processing', dur: 600 },
  { k: 'lv3', th: 75, l: 'finalizing', dur: 600 },
  { k: 'cola', th: 90, l: 'blooming', dur: 1200 },
  { k: 'halo', th: 100, l: 'ready', dur: 500 },
];
